import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2 } from "lucide-react";
import { useLang, useT } from "../../i18n";
import { getCart, saveCart, cartTotal } from "../../lib/shopApi";

export const CartDrawer = ({ open, onClose, onCheckout }) => {
  const t = useT();
  const lang = useLang();
  const cart = getCart();

  const removeItem = (key) => saveCart(cart.filter((i) => i.key !== key));

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-bergamote-900/40 backdrop-blur-sm z-[75]"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-cream-50 z-[80] flex flex-col shadow-2xl"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-[64px] border-b border-bergamote-600/15">
              <p className="font-serif text-xl font-semibold text-bergamote-900">{t.sh_cart}</p>
              <button onClick={onClose} data-testid="cart-close" aria-label="Close cart" className="w-10 h-10 rounded-full border border-bergamote-600/30 flex items-center justify-center text-bergamote-800 hover:bg-bergamote-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-lenis-prevent>
              {cart.length === 0 && <p className="text-bergamote-700/70 text-sm text-center pt-10" data-testid="cart-empty">{t.sh_empty}</p>}
              {cart.map((item) => (
                <div key={item.key} className="flex gap-4 items-center border-b border-bergamote-600/10 pb-4" data-testid={`cart-item-${item.key}`}>
                  {item.image && <img src={item.image} alt="" className="w-16 h-16 rounded-2xl object-cover" />}
                  <div className="flex-1">
                    <p className="font-serif font-semibold text-bergamote-900">{lang === "fr" ? item.name_fr : item.name_en}</p>
                    <p className="text-xs text-bergamote-700/70 font-mono">{item.size_label} × {item.quantity}</p>
                  </div>
                  <p className="font-mono text-sm text-bergamote-900">CHF {(item.unit_price * item.quantity).toFixed(2)}</p>
                  <button onClick={() => removeItem(item.key)} data-testid={`cart-remove-${item.key}`} aria-label={t.sh_remove} className="text-bergamote-400 hover:text-destructive transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-bergamote-600/15 px-6 py-5 space-y-4">
                <div className="flex justify-between font-mono">
                  <span className="text-bergamote-700">{t.sh_subtotal}</span>
                  <span className="font-semibold text-bergamote-900" data-testid="cart-subtotal">CHF {cartTotal().toFixed(2)}</span>
                </div>
                <button
                  data-testid="cart-checkout-btn"
                  onClick={onCheckout}
                  className="w-full rounded-full bg-saffron-500 text-bergamote-900 py-3.5 text-sm font-bold hover:bg-saffron-400 transition-colors duration-300"
                >
                  {t.sh_checkout}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
